import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { GalleryDto, ImageDto, ImageVersionDto } from '@picflow/types';
import { BehaviorSubject, Observable, Subject, Subscriber, Subscription, lastValueFrom } from 'rxjs';
import { OutOfStorageDialogComponent } from '../account/dialogs/out-of-storage-dialog/out-of-storage-dialog.component';
import { ChecklistService } from '../checklist.service';
import { UploadObj, UploadObjError } from '../classes/upload-obj.class';
import {
  allowedRawTypes,
  allowedTypes,
  maxPostImageRetryCountWithDelay,
  maxUploadRetryCount,
  postImageRetryDelay,
  waitForImageProcessingTimeout,
} from '../consts';
import { sortImageSection } from '../gallery/lib/sort-image-section';
import { calcBasePath } from '../lib/calc-base-url';
import { getPermissions } from '../lib/get-allowed-feedback-options';
import { getFileContentType } from '../lib/get-file-type';
import { randomString } from '../lib/random-string';
import { removeExtension } from '../lib/remove-extension';
import { sleep } from '../lib/sleep';
import { AppConfigService } from '../providers/config.service';
import { AnalyticsService } from './analytics.service';
import { DialogService } from './dialog.service';
import { DuplicateImageService } from './duplicate-image.service';
import { ExifService } from './exif.service';
import { FeatureFlagsService } from './feature-flags.service';
import { ImageService } from './image.service';
import { NetworkService } from './network.service';
import { SocketService } from './socket.service';
import { StateService } from './state.service';
import { ToastService } from './toast.service';

@Injectable({
  providedIn: 'root',
})
export class UploadService {
  private pending: { upload: UploadObj; resolve: (value: unknown) => void }[] = [];
  private activeUploads: Set<UploadObj> = new Set();
  public uploads: UploadObj[] = [];
  private _uploadEvents: Subject<UploadObj> = new BehaviorSubject<UploadObj>(undefined);
  public readonly onUploadEvent: Observable<UploadObj> = this._uploadEvents.asObservable();
  private readonly maxConcurrentUploads = 5;
  public currentUploadSpeed: number;

  constructor(
    private http: HttpClient,
    private config: AppConfigService,
    private dialog: DialogService,
    private imageService: ImageService,
    private socketService: SocketService,
    private networkService: NetworkService,
    private state: StateService,
    private analytics: AnalyticsService,
    private toasts: ToastService,
    private duplicateService: DuplicateImageService,
    private checklistService: ChecklistService,
    private featureFlags: FeatureFlagsService,
    private exifService: ExifService
  ) {}

  public async doUpload(
    upload: UploadObj,
    markAsCompleted = false
  ): Promise<{ key: string; uuid: string; timeTaken?: number }> {
    this.uploads.push(upload);
    this._uploadEvents.next(upload);

    if (!this.allowedFileType(upload.file, upload.gallery)) {
      upload.status = 'error';
      upload.error = 'invalid-file-type';
      upload.progress = 100;
      return { key: undefined, uuid: undefined };
    }

    await this.onUploadAllowed(upload);
    const startTime = Date.now();
    let endTime: number;

    // For now we just set the progress to 50%
    // The loader will transition slowly to this value
    upload.progress = 75;

    let errorType: UploadObjError;

    let key: string;
    let uuid: string;
    if (upload.cancelled) {
      this.markUploadAsCancelled(upload);
      return { key: undefined, uuid: undefined };
    } else if (!upload.file.size) {
      this.markUploadAsError(upload, 'cant-read-file');
      this._uploadEvents.next(upload);
    } else {
      upload.status = 'started';
      upload.startTime = Date.now();
      this._uploadEvents.next(upload);
      // upload image
      try {
        uuid = await this.uploadGalleryImage(upload);
      } catch (err) {
        console.log(
          upload,
          upload.file.name,
          upload.file.size,
          upload.file.type,
          upload.file.webkitRelativePath,
          upload.file.lastModified
        );
        if (err.error?.includes?.('EntityTooSmall')) errorType = 'cant-read-file';

        setTimeout(() => {
          // throwing error async so that it doesn't break the upload
          throw err.message || err;
        });
      }
      if (!uuid) {
        this.markUploadAsError(upload, errorType);
        this._uploadEvents.next(upload);
      } else {
        endTime = Date.now();
        this.markUploadAsProcessing(upload);
        const resp = await this.waitForImageProcessing(uuid);
        if (resp) {
          key = calcBasePath(uuid);
        } else {
          uuid = undefined;
          this.markUploadAsError(upload);
          this._uploadEvents.next(upload);
        }
      }
    }
    if (markAsCompleted && key && uuid) {
      this.markUploadAsDone(upload);
      this._uploadEvents.next(upload);
    }

    return { key, uuid, timeTaken: endTime - startTime };
  }

  private startAllowedPending() {
    if (this.activeUploads.size >= this.maxConcurrentUploads) return;
    if (!this.pending.length) return;
    const winner = this.pending.shift();
    this.activeUploads.add(winner.upload);
    winner.resolve(true);
    this.startAllowedPending();
  }

  private async onUploadAllowed(upload: UploadObj) {
    let promise = new Promise(resolve => this.pending.push({ upload, resolve }));
    this.startAllowedPending();
    return promise;
  }

  private markUploadAsError(upload: UploadObj, error: UploadObjError = 'general') {
    upload.status = 'error';
    upload.error = error;
    upload.progress = 100;
    this.activeUploads.delete(upload);
    this.startAllowedPending();
  }

  private markUploadAsCancelled(upload: UploadObj) {
    upload.status = 'error';
    upload.error = 'cancelled';
    upload.progress = 100;
    this.activeUploads.delete(upload);
    this.startAllowedPending();
  }

  private markUploadAsProcessing(upload: UploadObj) {
    upload.endTime = Date.now();
    upload.status = 'processing';
    upload.progress = 100;
    this.activeUploads.delete(upload);
    this.startAllowedPending();
  }

  private markUploadAsDone(upload: UploadObj) {
    upload.status = 'done';
    upload.progress = 100;
  }

  private async waitForImageProcessing(uuid: string): Promise<boolean> {
    const start = Date.now();
    this.socketService.connect();
    let handled = false;
    return new Promise(async resolve => {
      let sub: Subscription;
      sub = this.socketService.onSocketEvent.subscribe(data => {
        if (!data) return;
        if (!['image.converted.fail', 'image.converted'].includes(data.name)) return;
        if (data.payload.imageUpload.uuid !== uuid) return;
        if (handled) return;
        handled = true;
        sub?.unsubscribe();
        if (data.name === 'image.converted.fail') {
          console.debug('Image failed to convert after ' + ((Date.now() - start) / 1000).toFixed(2) + ' seconds', uuid);
          resolve(false);
        }
        if (data.name === 'image.converted') {
          console.debug('Image converted after ' + ((Date.now() - start) / 1000).toFixed(2) + ' seconds', uuid);
          resolve(true);
        }
      });
      await sleep(waitForImageProcessingTimeout);
      if (handled) return; // avoid pointless network checks
      await this.networkService.onNetworkOnline();
      await sleep(500);
      if (handled) return;
      handled = true;

      // If we get to this point, either the websocket is not connected anymore or the conversion event has failed
      // To check if the conversion worked, we check if the base image already exists
      const resp = await this.imageService.checkIfImageExists(uuid);
      if (resp) {
        console.debug('Image converted after websocket timeout');
        await sleep(2000); // waiting again.. maybe needed
        return resolve(true);
      }

      sub?.unsubscribe();
      console.debug(
        'Stopped waiting for image conversion after ' + ((Date.now() - start) / 1000).toFixed(2) + ' seconds'
      );
      setTimeout(() => {
        throw new Error(
          'Stopped waiting for image conversion after ' + ((Date.now() - start) / 1000).toFixed(2) + ' seconds'
        );
      });
      resolve(false);
    });
  }

  public removeUploads(galleryId: string) {
    this.uploads = this.uploads.filter(upload => upload.galleryId !== galleryId);
  }

  public cancelUploads(galleryId: string) {
    this.uploads.filter(upload => upload.galleryId === galleryId).forEach(upload => (upload.cancelled = true));
  }

  public allowedFileType(file: File, gallery: GalleryDto): boolean {
    if (!file) return false;

    if (this.featureFlags.get('rawUpload')) {
      allowedTypes.push(...allowedRawTypes);
    }

    const contentType = getFileContentType(file);
    if (contentType.split('/')[0] !== 'image') return false;
    const type = contentType.split('/')[1].toLowerCase();

    const permissions = getPermissions(gallery, this.state.plan);

    if (allowedRawTypes.includes(type) && !permissions.image.uploadRaw) {
      console.log('File format not allowed due to plan', type);
      return false;
    }

    return allowedTypes.includes(type);
  }

  private async uploadGalleryImage(upload: UploadObj, retryCount = 0): Promise<string> {
    if (retryCount > 0) await this.networkService.onNetworkOnline();
    const contentType = getFileContentType(upload.file);

    const params = { contentLength: upload.file.size, contentType, gallery: upload.galleryId };
    let response: any;
    try {
      response = await lastValueFrom(this.http.post(this.config.apiEndpoint + '/images/upload', params));
    } catch (err) {
      if (retryCount > maxUploadRetryCount) throw err;
      return this.uploadGalleryImage(upload, retryCount + 1);
    }
    const formData = new FormData();
    Object.entries(response.fields).forEach(([field, value]: any) => formData.append(field, value));
    formData.append('file', upload.file);

    try {
      await lastValueFrom(this.http.post(response.upload_url, formData));
    } catch (err) {
      if (err.error?.includes?.('EntityTooSmall')) {
        // try {
        //   console.log('Manually re-creating file to try uploading that way');
        //   await fileToBase64(upload.file);
        //   console.log('Successfully re-created file, which could be uploaded');
        // } catch (err) {
        //   console.log('got error during b64 converting file');
        //   console.log(err);
        // }
        throw err;
      }
      if (retryCount > maxUploadRetryCount) {
        throw err;
      }
      return this.uploadGalleryImage(upload, retryCount + 1);
    }

    // Slowly transition to 100%
    upload.progress = 100;

    return response.fields.key.split('original/')[1].split('.')[0];
  }

  public async uploadAvatar(file: File): Promise<string> {
    const params = {
      contentType: file.type,
      contentLength: file.size,
    };

    const response = <any>(
      await lastValueFrom(this.http.post(this.config.apiEndpoint + '/images/upload/avatar', params))
    );
    const formData = new FormData();
    Object.entries(response.fields).forEach(([field, value]: any) => formData.append(field, value));
    formData.append('file', file);

    await lastValueFrom(this.http.post(response.upload_url, formData));

    const uuid = response.fields.key.split('original/')[1].split('.')[0];
    await this.waitForImageProcessing(uuid);
    return uuid;
  }

  public async uploadWatermark(file: File): Promise<string> {
    const params = {
      contentType: file.type,
      contentLength: file.size,
    };

    const response = <any>(
      await lastValueFrom(this.http.post(this.config.apiEndpoint + '/images/upload/watermark', params))
    );
    const formData = new FormData();
    Object.entries(response.fields).forEach(([field, value]: any) => formData.append(field, value));
    formData.append('file', file);

    await lastValueFrom(this.http.post(response.upload_url, formData));

    const uuid = response.fields.key.split('original/')[1].split('.')[0];
    await this.waitForImageProcessing(uuid);
    return uuid;
  }

  public handleCanUpload(files: File[]) {
    const resp = this.canUpload(files);
    if (!resp) return;
    if (!resp.possible) {
      this.dialog.open(OutOfStorageDialogComponent, { over: resp.over });
      return false;
    }
    return true;
  }

  private calcNewPositions(images: ImageDto[], files: File[], sorting: string) {
    if (sorting === 'manual') {
      let highest = Math.max(...images.map(image => image.position));
      const map = new Map();
      files.forEach(file => {
        highest += 1;
        map.set(file.name, highest);
      });
      return map;
    }
    const allImages = images.concat(
      files.map(
        file =>
          <ImageDto>{
            id: randomString(12),
            position: 9999999999,
            version_data: { image_name: file.name, created_at: Date.now() },
          }
      )
    );
    const sortedImages = sortImageSection(allImages, sorting || 'manual');
    const map: Map<string, number> = new Map();
    const positions = this.imageService.calcOrderChanges(sortedImages);

    sortedImages.forEach((image, i) => {
      const position = positions.find(pos => pos.id === image.id);
      map.set(image.version_data.image_name, position?.pos ?? i);
    });

    return map;
  }

  private async internalManagedUpload(
    observer: Subscriber<{ type: string; data?: any }>,
    _files: File[],
    gallery: GalleryDto,
    sectionId: string,
    allImages: ImageDto[],
    sorting: string
  ) {
    // Getting files / checking for duplicates
    const { files, imagesToReplace } = await this.duplicateService.handleDuplicateFiles(
      _files,
      gallery.id,
      this.uploads
    );

    if (!files?.length) {
      observer.next({ type: 'done', data: [] });
      observer.complete();
      return;
    }

    const resp = this.handleCanUpload(files);
    if (!resp) {
      observer.next({ type: 'done', data: [] });
      observer.complete();
      return;
    }

    const positionMap: Map<string, number> = this.calcNewPositions(allImages, files, sorting);
    const uploads = files.map(file => new UploadObj(file, gallery, sectionId, positionMap.get(file.name)));

    observer.next({ type: 'started', data: { files, imagesToReplace, uploads } });
    this.analytics.track('upload_started');

    window.onbeforeunload = function (event: any) {
      var e = event || window.event;
      if (e) e.returnValue = 'Are you sure? The upload is not completed yet.';
      return 'Are you sure? The upload is not completed yet.';
    };

    const images = (
      await Promise.all(
        uploads.map(async upload => {
          const { key, uuid, timeTaken } = await this.doUpload(upload);
          if (!uuid) {
            observer.next({ type: 'failed', data: upload });
            return;
          }

          let image: ImageDto;
          let version: ImageVersionDto;
          try {
            const nameWithoutExtension = removeExtension(upload.file.name);
            const imageToReplace = imagesToReplace.find(
              i => removeExtension(i.version_data.image_name) === nameWithoutExtension
            );
            if (imageToReplace) {
              version = await this.postVersion(imageToReplace.id, uuid, upload);
            } else {
              image = await this.postImage(key, gallery.id, sectionId, uuid, upload);
              // const exif = this.exifService.getExif(image);
              // if (exif.rating === 5) {
              //   // await this.imageService.updateImage(image.id, {rating: 5}, image.gallery, image.section);
              // }
            }
          } catch (err) {
            console.log('there was actually an error');
            this.markUploadAsError(upload);
            this._uploadEvents.next(upload);
            observer.next({ type: 'failed', data: upload });
            setTimeout(() => {
              throw err.message ?? err;
            });
            return;
          }
          if (image || version) {
            if (!upload.cancelled) {
              observer.next({ type: 'uploaded', data: { image, version, upload } });
            }
            return image;
          }
        })
      )
    ).filter(img => img);

    // Checking if the user has manually cancelled the upload
    const cancelled = uploads.find(upload => upload.cancelled);
    if (cancelled) {
      this.analytics.track('upload_cancelled');
      this.toasts.add('Upload cancelled', 'success');

      // TODO: need to figure out how to "delete the image version"
      await Promise.all(images.map(image => this.imageService.deleteImage(image)));
      observer.next({ type: 'cancelled', data: uploads });
      observer.next({ type: 'done', data: uploads });
      observer.complete();
      window.onbeforeunload = null;
      return;
    }

    window.onbeforeunload = null;

    this.analytics.track('upload_success');
    observer.next({ type: 'done', data: uploads });
    observer.complete();
    this.checklistService.markItemAsDone('uploadImages');
  }

  async postImage(key: string, galleryId: string, sectionId: string, uuid: string, upload: UploadObj, retryCount = 0) {
    if (retryCount > 0) await this.networkService.onNetworkOnline();
    try {
      const resp = await this.imageService.postImage({
        galleryId,
        sectionId,
        uuid,
        name: upload.file.name,
        position: upload.position,
        description: upload.description,
      });
      this.markUploadAsDone(upload);
      this._uploadEvents.next(upload);
      return resp;
    } catch (err) {
      if (retryCount > 0) {
        if (!(err instanceof HttpErrorResponse)) throw err;
        if (err.status !== 500) throw err;
        if (err.error.message !== 'Image upload not exists or not ready yet. Please try again later') throw err;
      }
      if (retryCount >= maxPostImageRetryCountWithDelay) throw err;
      console.debug('image post failed, retrying in a few seconds', uuid);
      await sleep(postImageRetryDelay);
      return this.postImage(key, galleryId, sectionId, uuid, upload, retryCount + 1);
    }
  }

  async postVersion(imageId: string, uuid: string, upload: UploadObj, retryCount = 0): Promise<ImageVersionDto> {
    if (retryCount > 0) await this.networkService.onNetworkOnline();
    try {
      const resp = await this.imageService.postVersion(imageId, {
        uuid,
        name: upload.file.name,
        galleryId: upload.galleryId,
      });
      this.markUploadAsDone(upload);
      this._uploadEvents.next(upload);
      return resp;
    } catch (err) {
      if (retryCount > 0) {
        if (!(err instanceof HttpErrorResponse)) throw err;
        if (err.status !== 500) throw err;
        if (err.error.message !== 'Image upload not exists or not ready yet. Please try again later') throw err;
      }
      if (retryCount >= maxPostImageRetryCountWithDelay) throw err;
      console.debug('image version post failed, retrying in a few seconds', uuid);
      await sleep(postImageRetryDelay);
      return this.postVersion(imageId, uuid, upload, retryCount + 1);
    }
  }

  private canUpload(files: File[]): { possible: boolean; over: number } {
    if (!this.state.user) return;
    if (!this.state.plan) return { possible: true, over: 0 };
    const toUpload = files.reduce((t, f) => t + f.size / 1024, 0); // total uploads size in kb
    const capacity = this.state.plan ? this.state.plan.limits.storage.active.total : 999999999; // capacity in kb
    const remainingCapacity = capacity - this.state.activeTenant['storage'].active; // remaining capacity in kb
    return { possible: toUpload < remainingCapacity, over: toUpload - remainingCapacity };
  }

  public managedUpload(
    _files: FileList,
    gallery: GalleryDto,
    sectionId: string,
    allImages: ImageDto[],
    sorting: string
  ) {
    return new Observable((observer: Subscriber<{ type: string; data?: any }>) => {
      const files = Array.from(_files);
      this.internalManagedUpload(observer, files, gallery, sectionId, allImages, sorting);
    });
  }
}
